/**
 * I profili tipici GSE — il livello più basso del dato energetico.
 *
 * Un profilo GSE non è una curva di kWh: è una serie di **coefficienti
 * adimensionali** che distribuiscono un totale mensile sulle ore del mese. Il
 * GSE li ricava dallo storico SII per il prelievo e dallo storico di produzione
 * per l'immissione, e li usa proprio dove noi ne abbiamo bisogno: quando la
 * misura oraria validata non c'è (Regole Operative CACER).
 *
 *     E_ora = E_mese × coefficiente_ora            (profili «M», monorari)
 *     E_ora = E_mese_fascia × coefficiente_ora     (profili «F», per fascia)
 *
 * I coefficienti di un mese sommano a 1 nei profili M e a 3 nei profili F —
 * uno per fascia — ed è la verifica che l'import esegue riga per riga.
 *
 * ── Come si legge un codice ─────────────────────────────────────────────────
 * Prima lettera: **P** prelievo puro · **I** immissione pura · **M** punto misto,
 * dove prelievo e immissione convivono. È la stessa tripartizione del ruolo del
 * socio — consumatore, produttore, prosumer — ed è per questo che il ruolo basta
 * a scegliere il ramo del codice.
 *
 * Ultima lettera: **M** monorario, un totale mensile solo · **F** misura per
 * fasce, con un totale per F1/F2/F3.
 */

/** Il verso del punto di misura a cui un profilo si applica. */
export type GseProfileDirection = 'prelievo' | 'immissione';

/** Come il totale mensile arriva: uno solo, o uno per fascia oraria. */
export type GseProfileBanding = 'monorario' | 'fasce';

export interface GseProfileDef {
	code: string;
	direction: GseProfileDirection;
	banding: GseProfileBanding;
	/** Cosa descrive, come lo direbbe chi guarda la scheda del socio. */
	label: string;
	/** La forma della giornata, in una riga. */
	shape: string;
}

/**
 * Il registro dei 20 profili dei due file GSE 2025 — dodici di prelievo e otto
 * di immissione.
 *
 * L'ordine è quello delle colonne dei file, prima il prelievo e poi
 * l'immissione: chi confronta un import con la sorgente li ritrova nella stessa
 * sequenza.
 */
export const GSE_PROFILES: readonly GseProfileDef[] = [
	// ── Prelievo ──────────────────────────────────────────────────────────────
	{
		code: 'PDMM',
		direction: 'prelievo',
		banding: 'monorario',
		label: 'Domestico puro',
		shape: 'Minimo di notte, picco marcato la sera fra le 19 e le 21',
	},
	{
		code: 'PDMF',
		direction: 'prelievo',
		banding: 'fasce',
		label: 'Domestico puro, per fasce',
		shape: 'Minimo di notte, picco marcato la sera fra le 19 e le 21',
	},
	{
		code: 'PAUM',
		direction: 'prelievo',
		banding: 'monorario',
		label: 'Altri usi puri (commercio e servizi)',
		shape: 'Consumo concentrato in orario di lavoro, tarda mattina e tardo pomeriggio',
	},
	{
		code: 'PAUF',
		direction: 'prelievo',
		banding: 'fasce',
		label: 'Altri usi puri, per fasce',
		shape: 'Consumo concentrato in orario di lavoro, tarda mattina e tardo pomeriggio',
	},
	{
		code: 'PIRM',
		direction: 'prelievo',
		banding: 'monorario',
		label: 'Infrastruttura di ricarica veicoli elettrici',
		shape: 'Bassa di notte, attività distesa da mattina a sera',
	},
	{
		code: 'PIRF',
		direction: 'prelievo',
		banding: 'fasce',
		label: 'Infrastruttura di ricarica, per fasce',
		shape: 'Bassa di notte, attività distesa da mattina a sera',
	},
	{
		code: 'PACM',
		direction: 'prelievo',
		banding: 'monorario',
		label: 'Punto con sistema di accumulo',
		shape: 'Piatto dentro il mese',
	},
	{
		code: 'PACF',
		direction: 'prelievo',
		banding: 'fasce',
		label: 'Punto con accumulo, per fasce',
		shape: 'Piatto dentro ciascuna fascia',
	},
	{
		code: 'MDMM',
		direction: 'prelievo',
		banding: 'monorario',
		label: 'Prelievo domestico su punto misto con FV',
		shape: 'Forte riduzione a mezzogiorno per l’autoconsumo, picco serale di rete',
	},
	{
		code: 'MDMF',
		direction: 'prelievo',
		banding: 'fasce',
		label: 'Prelievo domestico su punto misto, per fasce',
		shape: 'Forte riduzione a mezzogiorno per l’autoconsumo, picco serale di rete',
	},
	{
		code: 'MAUM',
		direction: 'prelievo',
		banding: 'monorario',
		label: 'Prelievo altri usi su punto misto con FV',
		shape: 'Riduzione a mezzogiorno, poi picco serale',
	},
	{
		code: 'MAUF',
		direction: 'prelievo',
		banding: 'fasce',
		label: 'Prelievo altri usi su punto misto, per fasce',
		shape: 'Riduzione a mezzogiorno, poi picco serale',
	},

	// ── Immissione ────────────────────────────────────────────────────────────
	{
		code: 'IFVM',
		direction: 'immissione',
		banding: 'monorario',
		label: 'Immissione fotovoltaica pura',
		shape: 'Campana solare: zero di notte, massimo intorno a mezzogiorno',
	},
	{
		code: 'IFVF',
		direction: 'immissione',
		banding: 'fasce',
		label: 'Immissione fotovoltaica pura, per fasce',
		shape: 'Campana solare: zero di notte, massimo intorno a mezzogiorno',
	},
	{
		code: 'IAFM',
		direction: 'immissione',
		banding: 'monorario',
		label: 'Immissione da altre fonti rinnovabili',
		shape: 'Ripartizione oraria piatta; il GSE la usa anche per il FV con accumulo',
	},
	{
		code: 'IAFF',
		direction: 'immissione',
		banding: 'fasce',
		label: 'Immissione da altre fonti, per fasce',
		shape: 'Ripartizione oraria piatta; il GSE la usa anche per il FV con accumulo',
	},
	{
		code: 'MFAM',
		direction: 'immissione',
		banding: 'monorario',
		label: 'Immissione FV su punto misto, categoria «a» / domestico',
		shape: 'Campana stretta di sola eccedenza intorno a mezzogiorno',
	},
	{
		code: 'MFAF',
		direction: 'immissione',
		banding: 'fasce',
		label: 'Immissione FV su punto misto domestico, per fasce',
		shape: 'Campana stretta di sola eccedenza intorno a mezzogiorno',
	},
	{
		code: 'MFDM',
		direction: 'immissione',
		banding: 'monorario',
		label: 'Immissione FV su punto misto, categoria «d» / altri usi',
		shape: 'Campana di eccedenza a mezzogiorno, corretta sui consumi non domestici',
	},
	{
		code: 'MFDF',
		direction: 'immissione',
		banding: 'fasce',
		label: 'Immissione FV su punto misto altri usi, per fasce',
		shape: 'Campana di eccedenza a mezzogiorno, corretta sui consumi non domestici',
	},
] as const;

export const GSE_PROFILE_BY_CODE: Record<string, GseProfileDef> = Object.fromEntries(
	GSE_PROFILES.map((p) => [p.code, p]),
);

/** I codici di un verso, nell'ordine del file GSE. */
export function gseProfileCodes(direction: GseProfileDirection): string[] {
	return GSE_PROFILES.filter((p) => p.direction === direction).map((p) => p.code);
}

/** Il verso del punto di misura, dal suo `tipo`. Solo `injection` immette. */
export function directionOfMeterTipo(tipo: string | null | undefined): GseProfileDirection {
	return tipo === 'injection' ? 'immissione' : 'prelievo';
}

/**
 * I profili che si possono **scegliere** per un punto di misura.
 *
 * Solo i monorari, cioè la metà dei venti. Le varianti per fascia vogliono tre
 * totali mensili separati (F1/F2/F3) che oggi nessuna sorgente ci dà — è la
 * semplificazione dichiarata in `deriveGseProfileCode` — e offrirle in una
 * tendina significherebbe far scegliere qualcosa che poi verrebbe distribuito
 * come se fosse monorario. Quando i totali per fascia arriveranno, qui cade il
 * filtro: l'import carica già tutti e venti i profili.
 */
export function selectableGseProfiles(direction: GseProfileDirection): GseProfileDef[] {
	return GSE_PROFILES.filter((p) => p.direction === direction && p.banding === 'monorario');
}

/**
 * Il codice scelto a mano, se è utilizzabile su questo punto di misura.
 *
 * Restituisce `null` — cioè «usa la deduzione» — anche quando il codice esiste
 * ma è del verso sbagliato: un `IFVM` su un punto di prelievo distribuirebbe i
 * consumi con la campana del fotovoltaico. Meglio ignorarlo che fidarsi, perché
 * la colonna è testo libero e può arrivare da un import o da una correzione a
 * mano in banca dati.
 */
export function validGseProfileCode(
	code: string | null | undefined,
	meterTipo: string | null | undefined,
): string | null {
	if (!code) return null;
	const def = GSE_PROFILE_BY_CODE[code];
	if (!def || def.direction !== directionOfMeterTipo(meterTipo)) return null;
	return def.code;
}

/**
 * Cosa serve sapere di un socio per dargli un profilo.
 *
 * Sono tutti campi che l'anagrafica ha già: il profilo si **deduce**, non si
 * sceglie a mano. È la condizione perché un socio appena creato abbia comunque
 * un livello tipico — con una tendina in più da compilare, i soci senza
 * profilo tornerebbero il giorno stesso.
 */
export interface GseProfileInput {
	/** Ruolo del socio: è la prima lettera del codice, P / I / M. */
	ruolo: 'consumer' | 'producer' | 'prosumer';
	/** Tipo di socio: `residential` è domestico, tutto il resto è «altri usi». */
	tipo?: string | null;
	/** Il socio ha un sistema di accumulo. */
	hasStorage?: boolean | null;
	/**
	 * Tipologia di cliente finale dichiarata sul POD (`pods.tipologia_utenza`).
	 * Quando c'è vince sull'approssimazione da `tipo`: è il dato che il § 2.2.3
	 * delle Regole Operative chiede, `tipo` ne era solo il sostituto.
	 */
	tipologiaUtenza?: string | null;
	/** Verso del punto di misura da profilare. */
	direction: GseProfileDirection;
}

/** `residential` è l'unico tipo domestico: gli altri sono attività. */
export function isDomestico(tipo?: string | null): boolean {
	return tipo === 'residential';
}

/**
 * Domestico o no, dalla tipologia dichiarata se c'è, altrimenti dal tipo di
 * socio. La ricarica veicoli non è domestica.
 */
export function isDomesticoPunto(tipologiaUtenza?: string | null, tipo?: string | null): boolean {
	return tipologiaUtenza ? tipologiaUtenza === 'domestico' : isDomestico(tipo);
}

/**
 * Il codice GSE di un punto di misura.
 *
 * Il ruolo del socio dà la prima lettera, il tipo dà l'uso, l'accumulo ha
 * profili propri e li scavalca entrambi.
 *
 * ── Cosa è normato e cosa abbiamo dedotto ──────────────────────────────────
 * Le Regole Operative CACER (Allegato 1, § 2.2.3) dicono che i profili standard
 * sono definiti «per ciascuna tipologia di cliente finale (definita dal TIT) e
 * per ciascuna tipologia di impianto/fonte primaria», e che la tipologia del
 * cliente il GSE la prende dal Registro Centrale Ufficiale via SII (art. 8.3
 * TIAD). Noi quella tipologia non ce l'abbiamo: `member.tipo` ne è
 * un'approssimazione — `residential` per il domestico, il resto «altri usi» —
 * e va sostituita dal dato RCU appena lo si potrà leggere.
 *
 * L'accumulo invece è normato in modo esplicito, e non dalle Regole Operative:
 * il § 2.2.3 rimanda alle «Regole tecniche per l'attuazione delle disposizioni
 * relative all'integrazione di sistemi di accumulo», dove si legge che il GSE
 * «ai fini della ripartizione oraria delle misure applicherà dei profili
 * standard, definiti dal medesimo, utilizzando la **ripartizione piatta per
 * fasce (F1, F2, F3)** previste dal TIS». Piatto, quindi — ed è la coppia
 * `PACM`/`PACF` dei file, dove la variante per fasce è quella che la norma
 * descrive per prima.
 *
 * ── Le due semplificazioni che restano ─────────────────────────────────────
 * 1. Usiamo sempre il **monorario**: la ripartizione piatta per fasce vuole tre
 *    totali mensili separati (F1/F2/F3) che oggi nessuna sorgente ci dà. Il
 *    nostro `PACM` è piatto sul mese invece che dentro ciascuna fascia. Quando
 *    i totali per fascia arriveranno, qui cambia il suffisso: l'import carica
 *    già tutti e venti i profili, «F» compresi.
 * 2. Su un punto **misto con accumulo** nessuno dei due documenti dice quale
 *    condizione prevalga — non esiste un codice di accumulo con la M iniziale.
 *    Qui vince l'accumulo, al prezzo che la prima lettera («P», prelievo puro)
 *    non descrive più il punto. L'alternativa contraddirebbe la regola esplicita
 *    del piatto, che ci è parsa la contraddizione peggiore.
 */
export function deriveGseProfileCode(input: GseProfileInput): string {
	const domestico = isDomesticoPunto(input.tipologiaUtenza, input.tipo);
	const misto = input.ruolo === 'prosumer';

	if (input.direction === 'prelievo') {
		if (input.hasStorage) return 'PACM';
		// La ricarica veicoli ha un profilo suo, e solo «puro»: i file GSE non hanno
		// un codice misto per la ricarica. Su un punto misto vince comunque la
		// tipologia, per la stessa ragione per cui vince l'accumulo sopra: è la
		// forma dichiarata del prelievo, e una campana domestica non la descrive.
		if (input.tipologiaUtenza === 'ricarica_veicoli') return 'PIRM';
		if (misto) return domestico ? 'MDMM' : 'MAUM';
		return domestico ? 'PDMM' : 'PAUM';
	}

	// Immissione. Il FV con accumulo sul profilo «altre fonti»: l'accumulo sposta
	// l'immissione fuori dalle ore di sole e la campana non descrive più quel
	// punto. Attenzione: questa è l'unica riga della funzione che **non** siamo
	// riusciti a ricondurre a un documento GSE — né le Regole Operative né le
	// Regole tecniche accumuli nominano `IAF` per il FV con batteria. Resta
	// coerente con la forma dei coefficienti (IAFM è piatto e non va mai a zero
	// di notte, IFVM sì), ma è una nostra lettura.
	if (input.hasStorage) return 'IAFM';
	if (misto) return domestico ? 'MFAM' : 'MFDM';
	return 'IFVM';
}

/**
 * I punti di misura che il ruolo del socio implica.
 *
 * È la regola che sostituisce i tre bottoni «Aggiungi misuratore»: un
 * consumatore ha un punto di prelievo, un produttore uno di immissione, un
 * prosumer tutti e due. L'accumulo non è un ruolo — è un'attrezzatura — e
 * segue la dichiarazione del socio.
 */
export function metersForRole(
	ruolo: 'consumer' | 'producer' | 'prosumer',
	hasStorage?: boolean | null,
): ('M1' | 'M2' | 'M3')[] {
	const kinds: ('M1' | 'M2' | 'M3')[] = [];
	if (ruolo === 'consumer' || ruolo === 'prosumer') kinds.push('M1');
	if (ruolo === 'producer' || ruolo === 'prosumer') kinds.push('M2');
	if (hasStorage) kinds.push('M3');
	return kinds;
}

/**
 * Consumo annuo di riferimento per tipo di socio, in kWh.
 *
 * È il numero che tiene in piedi il livello tipico finché il socio non dichiara
 * il proprio: senza un totale da distribuire i coefficienti GSE non producono
 * kWh, e il POD resterebbe senza livello 4 — cioè con buchi nella curva, che è
 * esattamente ciò che il livello tipico esiste per impedire.
 *
 * Ordini di grandezza da consumi medi nazionali (ARERA, consumo domestico tipo
 * 2.700 kWh/anno); vanno sostituiti dal dato vero appena arriva, e il livello
 * stimato lo fa da sé non appena c'è una misura reale vicina.
 */
export const DEFAULT_ANNUAL_KWH: Record<string, number> = {
	residential: 2700,
	sme: 15_000,
	public_administration: 30_000,
	ets: 8_000,
	// Ufficio, negozio e fabbrica non hanno un ordine di grandezza loro: partono
	// da quello dell'impresa finché il socio non dichiara il suo consumo.
	office: 15_000,
	shop: 15_000,
	factory: 15_000,
};

export const DEFAULT_ANNUAL_KWH_FALLBACK = 3_000;

/**
 * Come il consumo annuo si distribuisce sui dodici mesi.
 *
 * I coefficienti GSE normalizzano **dentro** il mese — sommano a 1 per ogni
 * mese — quindi non dicono nulla su quanto pesi gennaio rispetto a luglio:
 * quella parte va messa qui, o dodici mesi diversi riceverebbero lo stesso
 * dodicesimo del consumo annuo.
 *
 * Il domestico ha il massimo d'inverno (illuminazione e riscaldamento
 * elettrico), gli altri usi d'estate (climatizzazione) con la caduta di agosto
 * delle chiusure. Ogni vettore somma a 1.
 */
export const MONTHLY_WEIGHTS: Record<'domestico' | 'altri_usi', number[]> = {
	// gen  feb   mar   apr   mag   giu   lug   ago   set   ott   nov   dic
	domestico: [0.098, 0.088, 0.086, 0.076, 0.073, 0.073, 0.079, 0.074, 0.073, 0.08, 0.093, 0.107],
	altri_usi: [0.085, 0.078, 0.081, 0.077, 0.084, 0.09, 0.098, 0.073, 0.086, 0.086, 0.081, 0.081],
};

/** I dodici totali mensili in kWh di un punto di prelievo. */
export function monthlyWithdrawalKwh(
	annualKwh: number,
	tipo?: string | null,
	tipologiaUtenza?: string | null,
): number[] {
	const weights =
		MONTHLY_WEIGHTS[isDomesticoPunto(tipologiaUtenza, tipo) ? 'domestico' : 'altri_usi'];
	return weights.map((w) => annualKwh * w);
}

/**
 * Resa mensile specifica del fotovoltaico in kWh/kWp (PVGIS, Italia centrale,
 * tilt 30° a sud, perdite di sistema 14%). Non dipende dalla taglia: moltiplica
 * i kWp installati e dà i dodici totali mensili del punto di immissione.
 */
export const PV_MONTHLY_KWH_PER_KWP = [68, 88, 118, 138, 156, 172, 182, 172, 138, 106, 72, 60];

/**
 * Potenza di riferimento quando il punto di immissione non ha ancora impianti
 * dichiarati, in kWp.
 *
 * Un M2 senza impianto esiste perché il socio si è dichiarato produttore, e
 * lasciarlo senza curva lo escluderebbe dal livello che non deve lasciare
 * buchi. Il numero è dichiaratamente provvisorio e sparisce al primo impianto
 * censito.
 */
export const DEFAULT_KWP: Record<'domestico' | 'altri_usi', number> = {
	domestico: 3,
	altri_usi: 20,
};

/** I dodici totali mensili in kWh di un punto di immissione. */
export function monthlyInjectionKwh(kwp: number): number[] {
	return PV_MONTHLY_KWH_PER_KWP.map((yield_) => kwp * yield_);
}
